import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { AdminAPI, Radiologist } from "@/app/apis/admin";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Radiologists() {
  const navigate = useNavigate();
  const [radiologists, setRadiologists] = useState<Radiologist[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRadiologists();
  }, []);

  const fetchRadiologists = async () => {
    try {
      setLoading(true);
      const data = await AdminAPI.getRadiologists();
      setRadiologists(data || []);
    } catch (err) {
      console.error("Failed to fetch radiologists:", err);
      toast.error("Failed to load radiologists data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Radiologists</h1>
        <p className="text-muted-foreground">
          All doctors with performance analytics
        </p>
      </div>

      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b">
              <tr>
                <th className="p-4 text-left">Name</th>
                <th className="p-4">Hospital</th>
                <th className="p-4">Studies</th>
                <th className="p-4">Sensitivity</th>
                <th className="p-4">Specificity</th>
                <th className="p-4">Accuracy</th>
              </tr>
            </thead>
            <tbody>
              {radiologists.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-muted-foreground">
                    No radiologists found
                  </td>
                </tr>
              ) : (
                radiologists.map((radiologist) => (
                  <tr
                    key={radiologist.id}
                    className="border-b cursor-pointer hover:bg-muted/30"
                    onClick={() => navigate(`/admin/radiologists/${radiologist.id}`)}
                  >
                    <td className="p-4 font-medium">{radiologist.name}</td>
                    <td className="p-4">{radiologist.hospital}</td>
                    <td className="p-4">{radiologist.studies}</td>
                    <td className="p-4">
                      <Badge variant="outline">
                        {(radiologist.sensitivity * 100).toFixed(1)}%
                      </Badge>
                    </td>
                    <td className="p-4">
                      <Badge variant="outline">
                        {(radiologist.specificity * 100).toFixed(1)}%
                      </Badge>
                    </td>
                    <td className="p-4">
                      <Badge variant="outline">
                        {radiologist.accuracy ? (radiologist.accuracy * 100).toFixed(1) : "—"}%
                      </Badge>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
